<template>
  <transition name="Viewer-">

    <div class="Viewer"
      v-show="isOpen"
      :class="{
        'Viewer--shiftHeader': shiftUiHeader,
        'Viewer--isFullScreen': screenfull.isFullscreen
      }">

      <div class="Viewer-container">

        <transition name="Viewer-ui-">

          <div class="Viewer-ui"
            v-show="showUi">

            <div class="Viewer-uiHeader">

              <div class="Viewer-uiButtons">

                <viewer-button class="Viewer-button"
                  v-show="gallery"
                  icon="grid"
                  @click.native="handleGridButtonClick"
                  title="View gallery"
                  small />

                <viewer-button class="Viewer-button"
                  v-show="screenfull.enabled && !screenfull.isFullscreen"
                  icon="fullscreen"
                  @click.native="handleFullscreenButtonClick"
                  title="Fullscreen"
                  small />

                <viewer-button class="Viewer-button"
                  icon="close"
                  @click.native="handleCloseButtonClick"
                  title="Close"
                  small />

              </div>

              <div class="Viewer-header"
                ref="header">

                <div class="Viewer-breadcrumbsAndCounter">

                  <breadcrumbs class="Viewer-breadcrumbs c-grey-xlt2"
                    v-if="breadcrumbs"
                    v-show="!shiftUiHeader"
                    :items="breadcrumbs"
                    trailing-caret />

                  <div class="Viewer-counter Viewer-headerCounter t-mono-xs c-grey-xlt2"
                    v-show="!shiftUiHeader">
                    {{ index + 1 }} OF {{ items.length }}
                  </div>

                </div>

              </div>

            </div>

            <div class="Viewer-uiContent"></div>

            <div class="Viewer-uiFooter">

              <div class="Viewer-footer"
                v-if="imageData[index]">

                <div class="Viewer-counter Viewer-footerCounter t-mono-xs c-grey-xlt2"
                  v-show="shiftUiHeader">
                  {{ index + 1 }} OF {{ items.length }}
                </div>

                <markdown class="Viewer-caption t-text-sm c-grey-xlt2"
                  v-if="imageData[index].caption"
                  :content="imageData[index].caption" />

                <p class="Viewer-filename t-mono-xxs c-grey-xlt2"
                  v-if="imageData[index].filename"
                  v-html="removeExtension(imageData[index].filename)">
                </p>

              </div>

            </div>

          </div>

        </transition>

        <div class="Viewer-slider">

          <div class="Viewer-sliderHeader"></div>

          <div class="Viewer-sliderContent"
            ref="slider">

            <div class="Viewer-prevNextButtons">

              <transition-group name="Viewer-prevNextButtons-">

                <viewer-button class="Viewer-button Viewer-previousButton"
                  key="viewerPreviousButton"
                  v-show="showUi && showPreviousButton"
                  @click.native="previous"
                  icon="arrow-left"
                  title="Previous image" />

                <viewer-button class="Viewer-button Viewer-nextButton"
                  key="viewerNextButton"
                  v-show="showUi && showNextButton"
                  @click.native="next"
                  icon="arrow-right"
                  title="Next image" />

              </transition-group>

            </div>

            <div class="Viewer-item"
              v-for="(item, index) in items">

              <div class="Viewer-image">

                <viewer-image class="Viewer-imageImage"
                  :smugmug="item"
                  :load="loadImage(index)"
                  @dataLoaded="handleImageDataLoaded(index, $event)" />

              </div>

            </div>

          </div>

          <div class="Viewer-sliderFooter"></div>

        </div>

      </div>

    </div>

  </transition>
</template>

<script>
import Flickity from 'flickity'
import screenfull from 'screenfull'
import { throttle } from 'lodash'

export default {
  name: 'Viewer',

  data () {
    return {
      /**
       * Will contain data for each image
       * @type {Array}
       */
      imageData: [],

      /**
       * Will contain data for the initial image
       * @type {Object}
       */
      initialImageData: {},

      /**
       * Whether to show the user interface
       * @type {Boolean}
       */
      showUi: true,

      /**
       * Hover position of the pointer, over the slider
       * @type {Object|String}
       */
      hover: false, // 'left', 'right'

      /**
       * Whether slider is being dragged
       * @type {Boolean}
       */
      dragging: false,

      /**
       * Whether to show the UI header in an alternate position
       * @type {Boolean}
       */
      shiftUiHeader: false
    }
  },

  computed: {
    window () { return this.$store.state.window },
    viewer () { return this.$store.state.viewer },
    isOpen () { return this.viewer.isOpen },
    items () { return this.viewer.items },
    index () { return this.viewer.index },
    breadcrumbs () { return this.viewer.breadcrumbs },
    gallery () { return this.viewer.gallery },
    screenfull () { return screenfull },

    /**
     * Whether to show the previous button
     */
    showPreviousButton () {
      if (this.dragging) return false
      return this.hover === 'left'
    },

    /**
     * Whether to show the next button
     */
    showNextButton () {
      if (this.dragging) return false
      return this.hover === 'right'
    }
  },

  watch: {
    /**
     * Close the viewer if isOpen changes to false
     */
    isOpen () {
      if (this.isOpen) return

      // Must match duration in .Viewer--enter
      setTimeout(this.destroy, 300)
    },

    /**
     * Initialize when items are populated
     */
    items () {
      if (!this.items.length) return
      this.$nextTick(this.init)
    },

    initialImageData () {
      this.$nextTick(() => {
        this.updateHash(this.index)
      })
    },

    /**
     * TODO: if we change to query params to handle permalinking,
     * this will need to change.
     */
    '$route.path' () {
      if (!this.initialImageData.id) return
      this.close()
    },

    // '$route.hash' () {
    //   if (this.$route.hash) return
    //   this.$nextTick(this.close)
    // },

    'window.width' () {
      this.shiftUnshiftUIHeader()
    },

    'window.height' () {
      this.shiftUnshiftUIHeader()
    }
  },

  created () {
    // Create throttled event handlers
    this.throttledHandleSliderMouseMove = throttle(this.handleSliderMouseMove, 250)
  },

  methods: {
    /**
     * Initialize viewer
     */
    init () {
      this.addEventListeners()
      this.showUi = true
      this.shiftUnshiftUIHeader()
      this.$store.dispatch('window/setPreventScroll', true)

      // Initialize imageData array
      this.items.forEach(item => {
        this.imageData.push({
          ratio: [],
          caption: false,
          filename: false
        })
      })

      this.$nextTick(this.initFlickity)
    },

    /**
     * Destroy viewer
     */
    destroy () {
      this.removeEventListeners()
      this.imageData = []
      if (!this.flickity) return
      this.flickity.off('select', this.handleSelect)
      this.flickity.off('staticClick', this.handleFlickityStaticClick)
      this.flickity.off('dragStart', this.handleDragStart)
      this.flickity.off('dragEnd', this.handleDragEnd)
      this.flickity.destroy()
    },

    /**
     * Add event listeners
     */
    addEventListeners () {
      document.addEventListener('keyup', this.handleKeyUp)
      this.$refs.slider.addEventListener('mouseenter', this.handleSliderMouseEnter)
      this.$refs.slider.addEventListener('mousemove', this.throttledHandleSliderMouseMove)
      this.$refs.slider.addEventListener('mouseleave', this.handleSliderMouseLeave)
      window.addEventListener('orientationchange', this.handleOrientationChange)
      if (screenfull.enabled) screenfull.on('change', this.handleScreenfullChange)
    },

    /**
     * Remove event listeners
     */
    removeEventListeners () {
      document.removeEventListener('keyup', this.handleKeyUp)
      this.$refs.slider.removeEventListener('mouseenter', this.handleSliderMouseEnter)
      this.$refs.slider.removeEventListener('mousemove', this.throttledHandleSliderMouseMove)
      this.$refs.slider.removeEventListener('mouseleave', this.handleSliderMouseLeave)
      window.removeEventListener('orientationchange', this.handleOrientationChange)
      if (screenfull.enabled) screenfull.off('change', this.handleScreenfullChange)
    },

    /**
     * Handle keyup event
     * @param  {Object} event
     */
    handleKeyUp (event) {
      switch (event.keyCode) {
        case 27: // esc
          this.close()
          break
      }
    },

    /**
     * Handle window orientationchange event
     */
    handleOrientationChange () {
      if (!this.flickity) return
      this.flickity.destroy()
      this.$nextTick(this.initFlickity)
    },

    /**
     * Handle screenfull change event
     */
    handleScreenfullChange (event) {
      this.shiftUnshiftUIHeader()
    },

    /**
     * Initialize Flickity
     */
    initFlickity () {
      if (!this.isOpen) return
      this.flickity = new Flickity(this.$refs.slider, {
        cellSelector: '.Viewer-item',
        setGallerySize: false, // Set height based on slider height
        prevNextButtons: false,
        pageDots: false,
        initialIndex: this.index
      })

      this.flickity.on('select', this.handleSelect)
      this.flickity.on('staticClick', this.handleFlickityStaticClick)
      this.flickity.on('dragStart', this.handleDragStart)
      this.flickity.on('dragEnd', this.handleDragEnd)
    },

    /**
     * Handle Flickity select event
     */
    handleSelect () {
      this.$store.dispatch('viewer/setIndex', this.flickity.selectedIndex)
      this.updateHash()
    },

    /**
     * Handle Flickity staticClick event
     */
    handleFlickityStaticClick (event, pointer, cellElem, cellIndex) {
      setTimeout(() => {
        this.showUi = !this.showUi
      }, 100)
    },

    /**
     * Handle Flickity dragStart event
     */
    handleDragStart () {
      this.dragging = true
    },

    /**
     * Handle Flickity dragEnd event
     */
    handleDragEnd () {
      this.dragging = false
    },

    /**
     * Handle the mouseenter event on the slider
     */
    handleSliderMouseEnter () {
      this.hover = true
    },

    /**
     * Handle the mouseleave event on the slider
     */
    handleSliderMouseLeave () {
      this.hover = false
    },

    /**
     * Handle mousemove event on slider
     */
    handleSliderMouseMove (event) {
      // Because the mousemove event is throttled, it may trigger after the
      // pointer has left the slider. Because of this, we need to bail if
      // the pointer has already left.
      if (!this.hover) return

      this.clientXPositionIsOnLeftSide(event.clientX)
        ? this.hover = 'left'
        : this.hover = 'right'
    },

    /**
     * Whether the given clientX is on the left side of the slider
     * @param {Number} clientX
     * @return {Boolean}
     */
    clientXPositionIsOnLeftSide (clientX) {
      return clientX < this.window.width / 2
    },

    /**
     * Update URL hash for permalinking
     */
    updateHash (initialIndex) {
      const index = initialIndex || this.flickity.selectedIndex
      const smugmugId = this.imageData[index].id
      this.$router.replace({ hash: smugmugId })
    },

    /**
     * Close viewer
     */
    close () {
      this.$store.dispatch('viewer/close')
      this.$store.dispatch('window/setPreventScroll', false)
      this.$router.replace({ hash: false })
      this.showButtons = false
    },

    /**
     * Handle close button click
     */
    handleCloseButtonClick () {
      if (!this.showUi) return
      if (this.screenfull.enabled) screenfull.exit()
      this.close()
    },

    /**
     * Handle fullscreen button click
     */
    handleFullscreenButtonClick () {
      if (!screenfull.enabled) return
      screenfull.toggle(this.$el)
    },

    /**
     * Handle grid button click
     */
    handleGridButtonClick () {
      this.close()
      this.$router.replace(this.gallery)
    },

    /**
     * Whether to load an image
     * @param {Integer} index
     * @return {Boolean}
     */
    loadImage (index) {
      // Index is current index
      if (index === this.index) return true

      // Index is next index
      if (index === this.index + 1) return true

      return false
    },

    /**
     * Handle image data loaded event
     * @param {Integer} index
     * @param {Object} $event
     */
    handleImageDataLoaded (index, $event) {
      if (!this.imageData[index]) return

      this.imageData[index].id = $event.id
      this.imageData[index].ratio = $event.ratio
      this.imageData[index].caption = $event.caption
      this.imageData[index].filename = $event.filename

      if (index === this.index) {
        this.initialImageData = {
          id: $event.id,
          ratio: $event.ratio,
          caption: $event.caption,
          filename: $event.filename
        }
      }
    },

    /**
     * Go to previous item
     */
    previous () {
      this.flickity.previous()
    },

    /**
     * Go to next item
     */
    next () {
      this.flickity.next()
    },

    /**
     * Remove extension from filename
     * @param {String} filename
     * @return {String}
     */
    removeExtension (filename) {
      if (!filename) return
      return filename.replace(/\.[^/.]+$/, '')
    },

    /**
     * Show or hide the breadcrumbs depending on available space
     */
    shiftUnshiftUIHeader () {
      this.shiftUiHeader = false
      this.$nextTick(() => {
        this.shiftUiHeader = this.shiftHeaderTest()
      })
    },

    /**
     * Test to decide whether to shift or unshift the UI header
     * @return {Boolean}
     */
    shiftHeaderTest () {
      // Minimum clearance above the header
      const min = 24

      // Test if header is touching the top of the viewport
      if (this.$refs.header.offsetTop <= min) return true

      // Test if viewport is 960 or less wide, so image is touching the sides
      if (this.window.width <= 960) return true

      // Test if we're in fullscreen mode
      if (this.screenfull.isFullscreen) return true

      return false
    }
  }
}
</script>

<style>
@import 'properties';
@import 'flickity/dist/flickity.css';

.Viewer {
  position: fixed;
  z-index: 900;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: var(--c-black);
  -webkit-font-smoothing: antialiased;

  @media (min-width: 1025px) {
    background-color: var(--c-black-tx);
  }
}

.Viewer--enter-active,
.Viewer--leave-active {
  transition: opacity 300ms ease;
}

.Viewer--enter,
.Viewer--leave-to {
  opacity: 0;
}

.Viewer-container {
  flex-grow: 1;
  position: relative;
  width: 100%;
  max-width: 960px;
}

.Viewer--isFullScreen .Viewer-container {
  max-width: none;
}

.Viewer-ui {
  position: absolute;
  z-index: 100;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

.Viewer-ui--enter-active,
.Viewer-ui--leave-active {
  transition: opacity 300ms ease;
}

.Viewer-ui--enter,
.Viewer-ui--leave-to {
  opacity: 0;
}

.Viewer-uiHeader,
.Viewer-sliderHeader {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  justify-content: flex-end;
  flex-basis: calc((100vh - (2/3 * 100vw)) / 2);
  max-height: 100px;

  @media (min-width: 960px) {
    flex-basis: calc((100vh - 640px) / 2);
    max-height: 192px; /* breadcrumbs should line up with other views 192 */
  }
}

.Viewer--isFullScreen .Viewer-uiHeader,
.Viewer--isFullScreen .Viewer-sliderHeader {
  flex-basis: unset;
}

/**
 * This creates a background for .Viewer-header
 * that is only visible when .Viewer-header is collapsed.
 */
.Viewer-uiHeader::after {
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  left: 0;
  display: block;
  content: '';
  height: 20px;
  background-image: linear-gradient(to bottom,
      rgba(0,0,0,0.25) 0%,
      rgba(0,0,0,0) 100%);
}

/**
 * Creates the same area as the image between the ui header and footer
 */
.Viewer-uiContent,
.Viewer-sliderContent {
  position: relative;
  flex-basis: calc(1 * 100vh); /*OLD: calc(2/3 * 100vw*/

  @media (min-width: 960px) {
    flex-basis: 640px;
  }
}

.Viewer-sliderContent .flickity-viewport {
  position: absolute;
  height: 100%;
  width: 100%;
}

.Viewer--isFullScreen .Viewer-uiContent,
.Viewer--isFullScreen .Viewer-sliderContent {
  flex-basis: 100vh;
}

.Viewer-uiFooter,
.Viewer-sliderFooter {
  flex-basis: calc((100vh - (2/3 * 100vw)) / 2);

  @media (min-width: 960px) {
    flex-basis: calc((100vh - 640px) / 2);
  }
}

.Viewer--isFullScreen .Viewer-uiFooter,
.Viewer--isFullScreen .Viewer-sliderFooter {
  flex-basis: unset;
}

.Viewer-slider {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100% !important;
}

.Viewer-item {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
}

.Viewer-image {
  display: flex;
  flex-grow: 1;
  height: auto;
  /* border: 2px solid turquoise; */
}

.Viewer-button {
  pointer-events: auto;
}

.Viewer-uiButtons {
  position: fixed;
  top: 0;
  right: 0;
  display: flex;
  padding: 0.5rem;
}

.Viewer-uiButtons * + * {
  margin: 0 0 0 var(--unit-025);
}

.Viewer-uiButtons .Button {
  border: none;
}

.Viewer-uiButtons .Button {
  @media (min-width: 960px) {
    width: 42px;
    height: 42px;
  }
}

.Viewer-uiButtons .Button .Icon {
  @media (min-width: 960px) {
    width: 18px !important;
    height: 18px !important;
  }
}

.Viewer-prevNextButtons {
  position: absolute;
  z-index: 999;
  top: 50%;
  display: none;
  width: 100%;
  pointer-events: none;

  @media (min-width: 960px) {
    display: block;
  }
}

.Viewer-prevNextButtons--enter-active,
.Viewer-prevNextButtons--leave {
  transition: opacity 0.25s ease;
}
.Viewer-prevNextButtons--enter,
.Viewer-prevNextButtons--leave-to {
  opacity: 0;
}

.Viewer-previousButton,
.Viewer-nextButton {
  position: absolute;
  top: 0;
  pointer-events: auto;
}

.Viewer-previousButton {
  left: 0;
  margin-left: var(--unit-025);

  @media (min-width: 1025px) {
    margin-left: -21px;
  }
}

.Viewer--isFullScreen .Viewer-previousButton {
  margin-left: var(--unit) !important;
}

.Viewer-nextButton {
  right: 0;

  margin-right: var(--unit-025);

  @media (min-width: 1025px) {
    margin-right: -21px;
  }
}

.Viewer--isFullScreen .Viewer-nextButton {
  margin-right: var(--unit) !important;
}

.Viewer-header {
  position: relative;
  z-index: 100;
  padding: 0 var(--unit-05); /* Do not add top/bottom padding here, messes with flex */

  @media (min-width: 960px) {
    padding: 0;
  }
}

.Viewer-breadcrumbsAndCounter {
  display: flex;

  /* Leading compensation */
  margin-bottom: 23px;
}

.Viewer-breadcrumbs {
  line-height: 1;
  color: var(--c-grey-xlt2);
}

.Viewer-breadcrumbs a {
  color: inherit;
}

.Viewer-breadcrumbs a:hover {
  color: var(--c-blue);
}

.Viewer-counter {

}

.Viewer-headerCounter {
  line-height: 1;
}

.Viewer-footer {
  position: relative;
  padding: var(--unit) var(--unit-05);

  @media (min-width: 960px) {
    padding: var(--unit-05) 0;
  }
}

.Viewer--isFullScreen .Viewer-footer {
  padding: var(--unit-2);
}

.Viewer-footer::after {
  position: absolute;
  z-index: -1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: block;
  content: '';
  background-image: linear-gradient(to top,
      rgba(0,0,0,1) 0%,
      rgba(0,0,0,0.5) 70%,
      rgba(0,0,0,0) 100%);

  @media (min-height: 900px) {
    display: none;
  }
}

.Viewer-caption {
  max-width: 640px;
  line-height: 18px;
}

.Viewer * + .Viewer-caption,
.Viewer-caption + * {
  margin-top: var(--unit-025);
  line-height: 18px;
}

.Viewer-filename {

}
</style>



// WEBPACK FOOTER //
// Viewer.vue?202d3ca2